<template>
    <div class="dashboard-container pa-4">
        <!-- Filtros del Dashboard -->
        <v-card class="mb-6 rounded-xl elevation-2 filter-panel" outlined>
            <v-card-text>
                <v-row dense align="center">
                    <v-col cols="12" md="3">
                        <s-select label="Año" :items="years" outlined hide-details dense v-model="filters.year"
                            @change="loadData" clearable />
                    </v-col>
                    <v-col cols="12" md="3">
                        <s-select-campaign-area label="Campaña" v-model="filters.cmaId" noDefault clearable typeArea="1"
                            full @change="loadData" />
                    </v-col>
                    <v-col cols="12" md="3">
                        <s-select-lot label="Lote / Sub-Lote" clearable v-model="filters.fltId" full noDefault
                            @change="loadData" />
                    </v-col>
                    <v-col cols="12" md="3" class="text-right">
                        <v-btn color="primary" class="rounded-lg" elevation="2" @click="loadData" :loading="loading">
                            <v-icon left>mdi-refresh</v-icon> Actualizar
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>

        <!-- KPIs Section -->
        <v-row dense class="mb-6">
            <v-col cols="12" sm="6" md="3">
                <v-card class="kpi-card gradient-blue rounded-xl" dark elevation="4">
                    <v-card-text class="d-flex align-center">
                        <div class="kpi-icon-container mr-4">
                            <v-icon size="40">mdi-chart-areaspline</v-icon>
                        </div>
                        <div>
                            <div class="text-caption text-uppercase font-weight-bold opacity-70">Total Proyectado</div>
                            <div class="text-h4 font-weight-black">{{ totals.projected | number }}</div>
                            <div class="text-caption mt-1">Jabas proyectadas</div>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
            <v-col cols="12" sm="6" md="3">
                <v-card class="kpi-card gradient-green rounded-xl" dark elevation="4">
                    <v-card-text class="d-flex align-center">
                        <div class="kpi-icon-container mr-4">
                            <v-icon size="40">mdi-check-circle-outline</v-icon>
                        </div>
                        <div>
                            <div class="text-caption text-uppercase font-weight-bold opacity-70">Total Real</div>
                            <div class="text-h4 font-weight-black">{{ totals.real | number }}</div>
                            <div class="text-caption mt-1">Jabas cosechadas</div>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
            <v-col cols="12" sm="6" md="3">
                <v-card class="kpi-card gradient-purple rounded-xl" dark elevation="4">
                    <v-card-text class="d-flex align-center">
                        <div class="kpi-icon-container mr-4">
                            <v-icon size="40">mdi-trending-up</v-icon>
                        </div>
                        <div>
                            <div class="text-caption text-uppercase font-weight-bold opacity-70">% Avance Global</div>
                            <div class="text-h4 font-weight-black">{{ totals.percentage.toFixed(1) }}%</div>
                            <v-progress-linear :value="totals.percentage" color="white" height="4" rounded class="mt-2"
                                opacity="0.3"></v-progress-linear>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
            <v-col cols="12" sm="6" md="3">
                <v-card class="kpi-card gradient-orange rounded-xl" dark elevation="4">
                    <v-card-text class="d-flex align-center">
                        <div class="kpi-icon-container mr-4">
                            <v-icon size="40">mdi-alert-circle-outline</v-icon>
                        </div>
                        <div>
                            <div class="text-caption text-uppercase font-weight-bold opacity-70">Pendiente</div>
                            <div class="text-h4 font-weight-black">{{ totals.pending | number }}</div>
                            <div class="text-caption mt-1">Faltan por cosechar</div>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- Charts Section -->
        <v-row dense class="mb-6">
            <v-col cols="12" lg="8">
                <v-card class="rounded-xl chart-card" elevation="2" outlined>
                    <v-card-title class="d-flex align-center py-4 px-6">
                        <div class="chart-title-icon mr-3">
                            <v-icon color="primary">mdi-chart-bar</v-icon>
                        </div>
                        <span class="text-h6 font-weight-bold">Comparativa Semanal: Proyectado vs Real</span>
                        <v-spacer></v-spacer>
                        <v-btn icon small color="grey" @click="resetDrilldown" v-if="isDrilled">
                            <v-icon>mdi-arrow-left</v-icon>
                        </v-btn>
                    </v-card-title>
                    <v-divider></v-divider>
                    <v-card-text class="pa-0">
                        <div v-if="loading" class="d-flex justify-center align-center" style="height: 400px;">
                            <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
                        </div>
                        <highcharts v-else :options="barChartOptions" ref="barChart" class="chart-container" />
                    </v-card-text>
                </v-card>
            </v-col>
            <v-col cols="12" lg="4">
                <v-card class="rounded-xl chart-card" elevation="2" outlined>
                    <v-card-title class="d-flex align-center py-4 px-6">
                        <div class="chart-title-icon mr-3">
                            <v-icon color="secondary">mdi-chart-pie</v-icon>
                        </div>
                        <span class="text-h6 font-weight-bold">Distribución de Cumplimiento</span>
                    </v-card-title>
                    <v-divider></v-divider>
                    <v-card-text class="pa-0">
                        <div v-if="loading" class="d-flex justify-center align-center" style="height: 400px;">
                            <v-progress-circular indeterminate color="secondary" size="64"></v-progress-circular>
                        </div>
                        <highcharts v-else :options="pieChartOptions" class="chart-container" />
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- Table Section -->
        <v-card class="rounded-xl elevation-3 table-card overflow-hidden" outlined>
            <v-card-title class="d-flex align-center py-5 px-6 bg-modern-header">
                <v-icon color="primary" class="mr-3" large>mdi-file-table-box-outline</v-icon>
                <div>
                    <div class="text-h6 font-weight-black primary--text">LISTADO DETALLADO DE PROYECCIONES</div>
                    <div class="text-caption grey--text text--darken-1">Seguimiento en tiempo real basado en registros
                        de producción</div>
                </div>
                <v-spacer></v-spacer>
                <v-text-field v-model="search" append-icon="mdi-magnify" label="Filtrar por lote, fundo, supervisor..."
                    single-line hide-details dense outlined
                    class="max-width-400 rounded-pill bg-white shadow-sm"></v-text-field>
            </v-card-title>
            <v-divider></v-divider>
            <v-data-table :headers="headers" :items="tableData" :loading="loading" :search="search"
                class="modern-premium-table elevation-0" :items-per-page="10"
                :footer-props="{ 'items-per-page-options': [10, 25, 50, -1], 'items-per-page-text': 'Filas por página:' }">

                <!-- Slot Lote y Fundo -->
                <template v-slot:item.lotInfo="{ item }">
                    <div class="py-3">
                        <div class="d-flex align-center mb-1">
                            <v-icon small color="primary" class="mr-2">mdi-hexagon-multiple</v-icon>
                            <span class="text-subtitle-1 font-weight-bold black--text">{{ item.FltName }}</span>
                        </div>
                        <div class="text-caption grey--text d-flex align-center">
                            <v-icon x-small color="grey" class="mr-1">mdi-office-building-marker</v-icon>
                            {{ item.FagName }}
                        </div>
                    </div>
                </template>

                <!-- Slot Semana y Variedad -->
                <template v-slot:item.weekInfo="{ item }">
                    <div class="py-3">
                        <v-chip small label color="indigo lighten-5"
                            class="indigo--text text--darken-4 font-weight-black mb-2">
                            {{ item.PdsName || 'Sem ' + item.WeekNumber }}
                        </v-chip>
                        <div class="d-flex align-center">
                            <v-badge dot inline color="green" class="mr-1"></v-badge>
                            <span class="text-caption font-weight-medium black--text">{{ item.TypeCultiveText }}</span>
                            <span class="mx-1 grey--text">|</span>
                            <span class="text-caption grey--text text--darken-2">{{ item.VrtDescription }}</span>
                        </div>
                    </div>
                </template>

                <!-- Slot Supervisor -->
                <template v-slot:item.SuperviceName="{ item }">
                    <div class="d-flex align-center py-3">
                        <v-avatar size="32" class="mr-3 gradient-avatar shadow-sm">
                            <span class="white--text font-weight-bold">{{ item.SuperviceName ?
                                item.SuperviceName.charAt(0) : '?' }}</span>
                        </v-avatar>
                        <div>
                            <div class="text-body-2 font-weight-bold black--text">{{ item.SuperviceName || 'Sin Asignar'
                            }}</div>
                            <div class="text-xxs grey--text">Responsable Técnico</div>
                        </div>
                    </div>
                </template>

                <!-- Slot Comparativa Numérica -->
                <template v-slot:item.comparison="{ item }">
                    <div class="py-3" style="min-width: 160px;">
                        <div class="d-flex justify-space-between align-end mb-2">
                            <div>
                                <div class="text-xxs grey--text text-uppercase font-weight-bold">Real</div>
                                <div class="text-subtitle-2 success--text font-weight-black">{{ item.WptRealJabas |
                                    number }}</div>
                            </div>
                            <div class="text-right">
                                <div class="text-xxs grey--text text-uppercase font-weight-bold">Meta</div>
                                <div class="text-subtitle-2 font-weight-black">{{ item.WptProjectedJabas | number }}
                                </div>
                            </div>
                        </div>
                        <v-progress-linear :value="item.pct" height="10" rounded :color="getProgressColor(item.pct)"
                            class="shadow-inner-sm bg-grey-lighten-4">
                        </v-progress-linear>
                    </div>
                </template>

                <!-- Slot Porcentaje de Avance -->
                <template v-slot:item.progress="{ item }">
                    <div class="text-right py-3 pr-2">
                        <div class="d-flex align-center justify-end">
                            <v-icon small :color="getProgressColor(item.pct)" class="mr-1">
                                {{ item.pct >= 100 ? 'mdi-trending-up' : (item.pct >= 50 ? 'mdi-trending-neutral' :
                                    'mdi-trending-down') }}
                            </v-icon>
                            <span class="text-h6 font-weight-black" :class="getProgressColor(item.pct) + '--text'">
                                {{ Math.round(item.pct) }}%
                            </span>
                        </div>
                        <v-chip x-small :color="getStatusColor(item.pct)"
                            class="white--text font-weight-black px-2 mt-1" elevation="0">
                            {{ getStatusText(item.pct).toUpperCase() }}
                        </v-chip>
                    </div>
                </template>

                <!-- Slot Empty -->
                <template v-slot:no-data>
                    <div class="pa-10 text-center">
                        <v-icon size="64" color="grey lighten-2">mdi-database-off</v-icon>
                        <div class="text-h6 grey--text mt-4">No se encontraron proyecciones con los filtros actuales
                        </div>
                    </div>
                </template>
            </v-data-table>
        </v-card>
    </div>
</template>

<script>
import Highcharts from 'highcharts';
import Highcharts3d from 'highcharts/highcharts-3d';
import { Chart } from 'highcharts-vue';
import WeeklyProjectionService from "@/services/Technicalassistance/WeeklyProjectionService";
import SSelectLot from "@/components/HarvestSchedule/SSelectLot.vue";
import SSelectCampaignArea from "@/components/Utils/SSelectCampaignArea.vue";

// Initialize 3D module
if (typeof Highcharts === 'object') {
    Highcharts3d(Highcharts);
}

export default {
    name: "WeeklyProjectionDashboard",
    components: {
        highcharts: Chart,
        SSelectLot,
        SSelectCampaignArea
    },
    filters: {
        number(val) {
            if (!val) return '0';
            return new Intl.NumberFormat('es-PE').format(val);
        }
    },
    data() {
        return {
            loading: false,
            search: '',
            isDrilled: false,
            years: [],
            filters: {
                year: new Date().getFullYear(),
                cmaId: null,
                fltId: ''
            },
            totals: {
                projected: 0,
                real: 0,
                percentage: 0,
                pending: 0
            },
            tableData: [],
            chartData: [],
            headers: [
                { text: 'IDENTIFICACIÓN LOTE', value: 'lotInfo', align: 'start', sortable: true, width: '25%' },
                { text: 'SEMANA / CULTIVO', value: 'weekInfo', align: 'start', sortable: true, width: '20%' },
                { text: 'RESPONSABLE', value: 'SuperviceName', align: 'start', sortable: true, width: '20%' },
                { text: 'PROYECCIÓN (REAL vs META)', value: 'comparison', align: 'start', sortable: false, width: '20%' },
                { text: 'CUMPLIMIENTO', value: 'progress', align: 'end', sortable: true, width: '15%' },
            ],
            barChartOptions: this.getInitialBarOptions(),
            pieChartOptions: this.getInitialPieOptions()
        };
    },
    async mounted() {
        this.years = this.$fun.getTop5LastYears?.() || [this.filters.year];
        await this.loadData();
    },
    methods: {
        getInitialBarOptions() {
            return {
                chart: {
                    type: 'column',
                    options3d: {
                        enabled: true,
                        alpha: 10,
                        beta: 15,
                        depth: 50,
                        viewDistance: 25
                    },
                    backgroundColor: 'transparent',
                    height: 400
                },
                title: { text: null },
                xAxis: {
                    categories: [],
                    crosshair: true,
                    labels: {
                        style: { color: '#64748b', fontSize: '11px', fontWeight: '600' }
                    }
                },
                yAxis: {
                    min: 0,
                    title: { text: 'Cantidad de Jabas', style: { fontWeight: '700' } },
                    gridLineColor: 'rgba(226, 232, 240, 0.6)'
                },
                tooltip: {
                    headerFormat: '<span style="font-size:12px; font-weight:700;">{point.key}</span><br/>',
                    pointFormat: '<span style="color:{series.color}">\u25CF</span> {series.name}: <b>{point.y:,.0f} jabas</b><br/>',
                    shared: true,
                    useHTML: true,
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: 12,
                    borderWidth: 0,
                    shadow: true
                },
                plotOptions: {
                    column: {
                        pointPadding: 0.2,
                        borderWidth: 0,
                        depth: 40,
                        borderRadius: 6,
                        cursor: 'pointer',
                        point: {
                            events: {
                                click: (e) => {
                                    this.handleChartClick(e.point.category);
                                }
                            }
                        }
                    }
                },
                series: [
                    { name: 'Proyectado (Meta)', data: [], color: '#6366f1' },
                    { name: 'Real (Cosecha)', data: [], color: '#10b981' }
                ],
                credits: { enabled: false }
            };
        },
        getInitialPieOptions() {
            return {
                chart: {
                    type: 'pie',
                    options3d: {
                        enabled: true,
                        alpha: 45,
                        beta: 0
                    },
                    backgroundColor: 'transparent',
                    height: 400
                },
                title: { text: null },
                tooltip: {
                    pointFormat: '{series.name}: <b>{point.percentage:.1f}%</b>'
                },
                plotOptions: {
                    pie: {
                        allowPointSelect: true,
                        cursor: 'pointer',
                        depth: 45,
                        dataLabels: {
                            enabled: true,
                            format: '<span style="font-size:11px; font-weight:700;">{point.name}</span><br/><span style="opacity:0.7">{point.percentage:.1f}%</span>',
                            useHTML: true,
                            distance: 20
                        }
                    }
                },
                series: [{
                    name: 'Participación',
                    colorByPoint: true,
                    data: []
                }],
                credits: { enabled: false }
            };
        },
        async loadData() {
            this.loading = true;
            try {
                const requestID = this.$fun.getUserID();

                const params = {
                    filter: {
                        WptYear: this.filters.year || 0,
                        CmaID: this.filters.cmaId || null,
                        FltID: "", // Cambio de '' a null para evitar error de nvarchar a int
                        PdsID: null,
                        TypeCultive: 0,
                        VrtID: null,
                        PrsSuperviceID: null
                    },
                    draw: 1,
                    start: 0,
                    length: -1,
                    order: [{ dir: "asc", column: "" }],
                    searchText: "",
                    IsLevelAdmin: 1
                };

                const resp = await WeeklyProjectionService.pagination(params, requestID);
                const rows = (resp.status === 200 && resp.data && resp.data.data) ? resp.data.data : [];
                console.log(JSON.parse(JSON.stringify({ resp })))

                this.processData(rows);
            } catch (error) {
                console.error("Error al procesar data de paginación:", error);
            } finally {
                this.loading = false;
            }
        },
        processData(rows) {
            let totalProj = 0;
            let totalReal = 0;
            const weekGroups = {};

            this.tableData = rows.map(r => {
                const proj = Number(r.WptProjectedJabas || 0);
                // Extraemos real de los campos que provee la paginación según la lógica del sistema
                const real = Number(r.WptRealJabas ?? r.HvsRealJabas ?? r.Real ?? 0);
                const pct = proj > 0 ? (real / proj) * 100 : (real > 0 ? 100 : 0);
                const weekLabel = r.PdsName || `Sem ${r.WeekNumber || 'N/A'}`;

                totalProj += proj;
                totalReal += real;

                if (!weekGroups[weekLabel]) {
                    weekGroups[weekLabel] = { proj: 0, real: 0 };
                }
                weekGroups[weekLabel].proj += proj;
                weekGroups[weekLabel].real += real;

                return {
                    ...r,
                    label: weekLabel,
                    WptProjectedJabas: proj,
                    WptRealJabas: real,
                    pct: pct > 100 ? 100 : pct
                };
            }).sort((a, b) => (Number(a.WeekNumber) || 0) - (Number(b.WeekNumber) || 0));

            this.totals.projected = totalProj;
            this.totals.real = totalReal;
            this.totals.pending = Math.max(0, totalProj - totalReal);
            this.totals.percentage = totalProj > 0 ? (totalReal / totalProj) * 100 : 0;

            const sortedWeeks = Object.keys(weekGroups).sort((a, b) => {
                // Intentar ordenar por número de semana si el label contiene "Sem XX"
                const numA = parseInt(a.replace(/\D/g, ''));
                const numB = parseInt(b.replace(/\D/g, ''));
                return numA - numB;
            });

            const categories = [];
            const seriesProj = [];
            const seriesReal = [];

            sortedWeeks.forEach(week => {
                categories.push(week);
                seriesProj.push(weekGroups[week].proj);
                seriesReal.push(weekGroups[week].real);
            });

            this.barChartOptions.xAxis.categories = categories;
            this.barChartOptions.series[0].data = seriesProj;
            this.barChartOptions.series[1].data = seriesReal;

            this.pieChartOptions.series[0].data = [
                { name: 'REALIZADO', y: totalReal, color: '#10b981' },
                { name: 'PENDIENTE', y: this.totals.pending, color: '#f59e0b' }
            ];
        },
        handleChartClick(category) {
            // Simular drilldown: filtrar tabla por la semana seleccionada
            this.search = category;
            this.isDrilled = true;
            this.$fun.alert(`Filtrando detalles para ${category}`, "info");
        },
        resetDrilldown() {
            this.search = '';
            this.isDrilled = false;
        },
        getProgressColor(pct) {
            if (pct >= 90) return 'success';
            if (pct >= 70) return 'primary';
            if (pct >= 40) return 'warning';
            return 'error';
        },
        getStatusColor(pct) {
            if (pct >= 100) return 'success';
            if (pct >= 80) return 'primary';
            if (pct > 0) return 'warning';
            return 'grey';
        },
        getStatusText(pct) {
            if (pct >= 100) return 'Completado';
            if (pct >= 80) return 'Aceptable';
            if (pct > 0) return 'En Proceso';
            return 'Sin Avance';
        }
    }
};
</script>

<style scoped>
.dashboard-container {
    background-color: #f1f5f9;
    min-height: 100vh;
}

.kpi-card {
    height: 130px;
    transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
    border: none !important;
}

.kpi-card:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.12) !important;
}

.kpi-icon-container {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 16px;
    padding: 12px;
    backdrop-filter: blur(4px);
}

.gradient-blue {
    background: linear-gradient(135deg, #4f46e5 0%, #3730a3 100%);
}

.gradient-green {
    background: linear-gradient(135deg, #10b981 0%, #065f46 100%);
}

.gradient-purple {
    background: linear-gradient(135deg, #8b5cf6 0%, #5b21b6 100%);
}

.gradient-orange {
    background: linear-gradient(135deg, #f59e0b 0%, #92400e 100%);
}

.filter-panel {
    background: rgba(255, 255, 255, 0.9) !important;
    backdrop-filter: blur(12px);
    border: 1px solid rgba(226, 232, 240, 0.8) !important;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1) !important;
}

.chart-card {
    background: white !important;
    border: 1px solid rgba(226, 232, 240, 0.8) !important;
    border-radius: 24px !important;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03) !important;
}

.chart-title-icon {
    background: #eff6ff;
    border-radius: 12px;
    padding: 8px;
}

.bg-modern-header {
    background-color: #f8fafc;
}

.table-card {
    background: white !important;
    border: 1px solid rgba(226, 232, 240, 0.8) !important;
    border-radius: 24px !important;
}

.modern-premium-table :deep(thead th) {
    background-color: #f8fafc !important;
    color: #64748b !important;
    font-weight: 800 !important;
    text-transform: uppercase;
    font-size: 0.7rem !important;
    letter-spacing: 0.07em;
    height: 56px !important;
    border-bottom: 2px solid #f1f5f9 !important;
}

.modern-premium-table :deep(tbody tr) {
    transition: background-color 0.2s ease;
}

.modern-premium-table :deep(tbody td) {
    border-bottom: 1px solid #f8fafc !important;
    height: 72px !important;
}

.modern-premium-table :deep(tbody tr:hover) {
    background-color: #f1f5f9 !important;
}

.gradient-avatar {
    background: linear-gradient(135deg, #6366f1 0%, #4338ca 100%);
}

.text-xxs {
    font-size: 10px;
    line-height: 1.2;
}

.shadow-inner-sm {
    box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.05);
}

.rounded-pill {
    border-radius: 50px !important;
}

.opacity-70 {
    opacity: 0.7;
}

.shadow-sm {
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.max-width-400 {
    max-width: 400px;
}
</style>
